import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { AssistantScreenContext } from "ai-assistant/screen-context/assistant-screen-context";

interface AssistantScreenContextRegistry {
  /** Declares `context` as what is on screen, and returns what withdraws it. */
  declare: (context: AssistantScreenContext) => () => void;
  /** What is on screen now, or undefined when no page has said. */
  read: () => AssistantScreenContext | undefined;
}

/**
 * Where pages declare what is on screen, and where the assistant reads it.
 *
 * Held in a ref rather than state. The assistant reads it once, when a message is sent, so a
 * page changing tab has no reason to re-render the assistant or anything else - and a provider
 * wrapping the whole page area that re-rendered on every declaration would re-render the page
 * that declared it.
 */
const Registry = createContext<AssistantScreenContextRegistry | undefined>(undefined);

export function AssistantScreenContextProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const current = useRef<{ owner: symbol; context: AssistantScreenContext } | undefined>(undefined);

  const registry = useMemo<AssistantScreenContextRegistry>(
    () => ({
      declare(context) {
        const owner = Symbol("assistant-screen-context");
        current.current = { owner, context };
        // Withdrawn only by whoever declared it. React runs the next page's effects after the
        // last one's cleanups, but a page re-declaring on a change runs its own cleanup first -
        // and in either order a stale cleanup must not erase a newer page's declaration.
        return () => {
          if (current.current?.owner === owner) current.current = undefined;
        };
      },
      read: () => current.current?.context,
    }),
    []
  );

  return <Registry.Provider value={registry}>{children}</Registry.Provider>;
}

/**
 * Tells the assistant what this page shows, for as long as the page is mounted.
 *
 * Pass undefined while there is nothing to say yet, such as before a record has loaded. The
 * value is compared by content, so a page may build it inline on every render. Outside the
 * provider this does nothing, which is where a page rendered without the app shell - a test, a
 * public page - is.
 */
export function useAssistantScreenContext(context: AssistantScreenContext | undefined): void {
  const registry = useContext(Registry);
  const key = context === undefined ? undefined : JSON.stringify(context);

  useEffect(() => {
    if (!registry || key === undefined) return;
    return registry.declare(JSON.parse(key) as AssistantScreenContext);
  }, [registry, key]);
}

/** Reads what is on screen at the moment of the call. For the assistant, when it sends a turn. */
export function useReadAssistantScreenContext(): () => AssistantScreenContext | undefined {
  const registry = useContext(Registry);
  return useCallback(() => registry?.read(), [registry]);
}
